import { historyTotals, settledBetNet } from './historyTotals';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, HelpCircle, List, X } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore } from '../../app/appStore';
import { fetchMyBets, settleMyRound, type BetDraw, type BetItem } from '../room/api';
import { roundsAwaitingSettlement } from './settleOnRead';
import { betProgram, gameNameKey, pickLabel } from '../../shared/game/betOptions';
import { gamingRound, programSlotSeconds, roundSeqFromRound } from '../../shared/game/roundLabel';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { brDayLabel, localClock, localDayKey, localDayShort } from '../../shared/lib/datetime';
import { fmtMoney } from '../../shared/lib/format';

/**
 * The player's bet record — one component, two entry points (room `+` menu and
 * Perfil → Histórico). They used to be two implementations of the same screen:
 * every fix landed on the Perfil one and the room one stayed on the 2026-06
 * layout, which is how it ended up showing gross payouts that did not add up to
 * its own round total, no draw result at all, and a By-round/All-bets switch the
 * Perfil version had already deleted for duplicating itself.
 *
 * The organising idea is that the numbers have to reconcile in both directions:
 * a bet row's net sums to the round footer, the round footers sum to the day
 * heading, and the days sum to the ledger at the top. Anything that cannot be
 * checked against the row above it does not belong here.
 */

const HISTORY_LIMIT = 100;

export type BetHistoryRoomContext = { code: string; label: string };

type Scope = { kind: 'all' } | { kind: 'room'; code: string; label: string } | { kind: 'game'; game: string };
type Filter = 'all' | 'win' | 'lose' | 'pending';

type UiBet = {
  id: string;
  round: number;
  roomCode: string;
  game: string;
  tab: string;
  pick: string;
  amount: number;
  payout: number;
  net: number;
  result: 'pending' | 'win' | 'lose' | 'cancelled';
  time: number;
  /**
   * The round's own anchor (`cutoff_ts`), not when the bet was placed.
   *
   * An hourly Milhar round takes bets for a whole hour and cuts off on the hour,
   * so a bet placed at 13:20 belongs to the round anchored at 14:00. Numbering it
   * from `placed_at` puts it in the previous hour and the ticket disagrees with
   * the results list by one. On the 5-minute games the two land in the same slot.
   */
  anchor: number;
  draw?: BetDraw;
};

type RoundGroup = {
  key: string;
  round: number;
  game: string;
  tab: string;
  time: number;
  anchor: number;
  stake: number;
  net: number;
  settled: boolean;
  draw?: BetDraw;
  bets: UiBet[];
};

type DayGroup = { key: string; time: number; net: number; settled: boolean; rounds: RoundGroup[] };

export function BetHistorySheet({
  token,
  roomContext,
  onClose,
}: {
  token: string;
  /** Present when opened from inside a room — adds the "this room" scope option. */
  roomContext?: BetHistoryRoomContext | null;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const setRoute = useAppStore((state) => state.setRoute);
  const setActiveArchiveRoundNumber = useAppStore((state) => state.setActiveArchiveRoundNumber);

  const [scope, setScope] = useState<Scope>({ kind: 'all' });
  const [filter, setFilter] = useState<Filter>('all');
  const [scopeOpen, setScopeOpen] = useState(false);

  // Narrowing to a room is the only scope the API itself understands, and that
  // is exactly what makes it useful: the same 100-row window then reaches
  // further back in time. Game scope is a local slice of what is already loaded.
  const roomFilter = scope.kind === 'room' ? scope.code : undefined;
  const [watching, setWatching] = useState(false);
  const query = useQuery({
    queryKey: ['bet-history', token, roomFilter || 'all'],
    queryFn: () => fetchMyBets(token, { limit: HISTORY_LIMIT, roomCode: roomFilter }),
    enabled: !!token,
    // Poll only while something is actually unsettled. This screen is where a
    // player watches a bet resolve — the card flips in place instead of a
    // full-screen takeover — but once every row is settled there is nothing left
    // to wait for, and this endpoint is not cheap.
    refetchInterval: watching ? 5_000 : false,
  });

  const allBets = useMemo(() => normalizeBets(query.data?.items || []), [query.data?.items]);
  const hasPending = allBets.some((bet) => bet.result === 'pending');
  if (hasPending !== watching) setWatching(hasPending);

  // Polling alone is not enough to flip a card: the server pays bets out on a
  // background settler that runs 1–2 minutes behind the public result, so the
  // poll kept re-reading "pending" long after the number was on every other
  // screen (D-49). The room page fixes this for itself by asking the server to
  // settle the player's bets the moment the draw lands; opened from Perfil the
  // sheet must do the same, or the card sits on PENDENTE for two minutes.
  // `roundsAwaitingSettlement` only names rounds whose draw the API already
  // shipped, and each round is asked once — the poll carries the rest.
  const queryClient = useQueryClient();
  const settleAttempts = useRef(new Set<number>());
  useEffect(() => {
    if (!token) return;
    const rounds = roundsAwaitingSettlement(allBets, settleAttempts.current);
    for (const round of rounds) {
      settleAttempts.current.add(round);
      settleMyRound(token, round)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['bet-history'] });
          // The payout moved the balance and the profile totals too.
          queryClient.invalidateQueries({ queryKey: ['my-bets'] });
          queryClient.invalidateQueries({ queryKey: ['me'] });
        })
        .catch(() => undefined);
    }
  }, [allBets, queryClient, token]);
  const scopedBets = useMemo(
    () => (scope.kind === 'game' ? allBets.filter((bet) => bet.game === scope.game) : allBets),
    [allBets, scope],
  );
  const bets = useMemo(
    () => scopedBets.filter((bet) => filter === 'all' || bet.result === filter),
    [scopedBets, filter],
  );
  const days = useMemo(() => groupByDay(groupByRound(bets)), [bets]);

  // Every figure on this screen describes WHAT IS ON SCREEN. Summing the whole
  // response while the list shows a filtered subset is how the old room sheet
  // ended up claiming "+78" on an account that was down R$ 17.
  const { stake, returned, net } = historyTotals(bets);
  const counts = useMemo(() => ({
    all: scopedBets.length,
    win: scopedBets.filter((bet) => bet.result === 'win').length,
    lose: scopedBets.filter((bet) => bet.result === 'lose').length,
    pending: scopedBets.filter((bet) => bet.result === 'pending').length,
  }), [scopedBets]);

  const games = useMemo(() => {
    const seen = new Map<string, number>();
    for (const bet of allBets) seen.set(bet.game, (seen.get(bet.game) || 0) + 1);
    return Array.from(seen.entries()).sort((a, b) => b[1] - a[1]);
  }, [allBets]);

  const truncated = allBets.length >= HISTORY_LIMIT;

  function openDraw(round: number) {
    if (!round) return;
    // Close first: a caller's onClose may restore its own route (Perfil sends
    // `bet-history` back to `me`), so setting the archive route before it would
    // just be overwritten inside the same batch.
    onClose();
    setActiveArchiveRoundNumber(String(round));
    setRoute('archive');
  }

  function applyScope(next: Scope) {
    setScope(next);
    setScopeOpen(false);
  }

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="profile-sheet bet-history-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t('meBetHistory')}
        onMouseDown={(event) => event.stopPropagation()}
        data-bet-history-sheet="true"
      >
        <div className="sheet-grabber" />

        <header className="sheet-header bh-header">
          <div>
            <h2>{t('meBetHistory')}</h2>
            <p>
              <button
                type="button"
                className={`bh-scope-pill${scopeOpen ? ' open' : ''}`}
                onClick={() => setScopeOpen((open) => !open)}
                aria-expanded={scopeOpen}
              >
                {scopeLabel(scope, t)} <i aria-hidden="true">{scopeOpen ? '⌃' : '⌄'}</i>
              </button>
            </p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {scopeOpen ? (
          <div className="bh-scope-menu" role="group" aria-label={t('historyScopeTitle')}>
            <ScopeRow active={scope.kind === 'all'} label={t('historyScopeAll')} onClick={() => applyScope({ kind: 'all' })} />
            {roomContext ? (
              <ScopeRow
                active={scope.kind === 'room'}
                label={t('historyScopeRoom').replace('%room%', roomContext.label)}
                onClick={() => applyScope({ kind: 'room', code: roomContext.code, label: roomContext.label })}
              />
            ) : null}
            {games.map(([game, count]) => (
              <ScopeRow
                key={game}
                active={scope.kind === 'game' && scope.game === game}
                label={t(gameNameKey(game))}
                count={count}
                onClick={() => applyScope({ kind: 'game', game })}
              />
            ))}
          </div>
        ) : null}

        {/* Stake includes pending bets; net and returned include settled bets only. */}
        <div className="me-sheet-summary history-summary">
          <div><span>{t('stake')}</span><strong>{fmtMoney(stake)}</strong></div>
          <div><span>{t('historyReturn')}</span><strong>{fmtMoney(returned)}</strong></div>
          <div><span>{t('net')}</span><strong className={netClass(net)}>{signed(net)}</strong></div>
        </div>

        <div className="history-filter-row">
          {(['all', 'win', 'lose', 'pending'] as Filter[]).map((item) => (
            <button key={item} type="button" className={filter === item ? 'active' : ''} onClick={() => setFilter(item)}>
              {t(filterLabelKey(item))} <i>{counts[item]}</i>
            </button>
          ))}
        </div>

        <div className="profile-sheet-list bh-list">
          {query.isLoading ? <SheetState icon={RefreshCw} title={t('loading')} /> : null}
          {query.isError ? (
            <SheetState icon={HelpCircle} title={t('historyLoadError')} actionLabel={t('retry')} onAction={() => query.refetch()} />
          ) : null}
          {!query.isLoading && !query.isError && scopedBets.length === 0 ? <SheetState icon={List} title={t('noBetsYet')} /> : null}
          {!query.isLoading && !query.isError && scopedBets.length > 0 && bets.length === 0 ? (
            <SheetState
              icon={List}
              title={t('historyFilterEmpty')}
              actionLabel={t('historyEmptySeeAll').replace('%n%', String(counts.all))}
              onAction={() => setFilter('all')}
            />
          ) : null}

          {days.map((day) => (
            <section className="bh-day" key={day.key}>
              <h3>
                <span>{dayHeading(day, t)}</span>
                <em aria-hidden="true" />
                {day.settled ? <u className={netClass(day.net)}>{signed(day.net)}</u> : null}
              </h3>
              {day.rounds.map((group, index) => (
                <Fragment key={group.key}>
                  {seamBefore(day.rounds, index) ? (
                    <p className="bh-seq-seam">
                      {t('historySeqDayBreak').replace('%d%', brDayLabel(gamingDayOf(group)).slice(0, 5))}
                    </p>
                  ) : null}
                  <RoundCard group={group} onOpenDraw={openDraw} />
                </Fragment>
              ))}
            </section>
          ))}

          {!query.isLoading && !query.isError && truncated ? (
            <p className="bh-trunc">
              {t('historyTruncated')}
              {roomContext && scope.kind === 'all' ? ` ${t('historyTruncatedRoomHint')}` : ''}
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function ScopeRow({ active, label, count, onClick }: { active: boolean; label: string; count?: number; onClick: () => void }) {
  return (
    <button type="button" className={active ? 'active' : ''} onClick={onClick} aria-pressed={active}>
      <span>{label}</span>
      {active ? <b aria-hidden="true">✓</b> : count === undefined ? null : <i>{count}</i>}
    </button>
  );
}

function RoundCard({ group, onOpenDraw }: { group: RoundGroup; onOpenDraw: (round: number) => void }) {
  const t = useI18nStore((state) => state.t);
  const slotSec = programSlotSeconds(betProgram(group.game, group.tab));
  // 每个游戏都用日内序号，Milhar 也不例外（2026-09-10 起）。
  //
  // 这里曾经给 Milhar 开过特例走全局期号，理由是「日内序号每天午夜归零，两天前
  // 的 #022 和今天的 #022 是两期，玩家拿回去核对会对不上」。那个顾虑是真的，但
  // 它对轮盘 / Sic Bo / 末位数一字不差地成立，而那三个游戏一直用日内序号 —— 特
  // 例让 Milhar 成了唯一不一致的那个，而不是唯一安全的那个（UX_REVIEW_2026-08-18
  // F3：同一注在游戏页叫 #15、这里叫 #005、账本页叫 #33324，一期三个号）。
  //
  // 顾虑的解法不是换一套号，是别让期号单独出现：这一行永远在某个日期分组底下
  // （HOJE / ONTEM / 02/09），日期 + 期号才是完整身份；要精确引用时，下面的
  // title 和详情页都带着全局 round_number。
  //
  // 期号本身锚定注册国时区（doc/TIME_DISPLAY_CONTRACT.md 规则 1），所以对同国
  // 所有玩家是同一个值 —— 正是「拿它去核对开奖」需要的那种可引用性。
  const seq = roundSeqFromRound({ starts_at: group.anchor || group.time }, slotSec)
    || String(group.round || '-');
  const parts = drawParts(group.game, group.draw, t);

  return (
    <article className={`bh-card${group.settled ? '' : ' live'}`}>
      <header>
        {/* 日内序号（5 分钟档 #001-288，整点档 #01-24），与房间／档案／钱包同一
            套身份。title 始终带全局期号，客服拿得到确切的一期。 */}
        <b className="bh-id" title={group.round ? `global #${group.round}` : undefined}>#{seq}</b>
        <span className="bh-game">{t(gameNameKey(group.game, group.tab))}</span>
        <span className="bh-time">{localClock(group.time)}</span>
        {/* Waiting is driven by the BET's state, not by whether we have the draw:
            a settled round served by a backend older than the draw payload has
            no chip at all, which is honest — it must never claim "pending" for
            a bet the player has already been paid on. */}
        {!group.settled ? (
          <span className="bh-waiting"><em aria-hidden="true" />{t('pending')}</span>
        ) : parts ? (
          <button
            type="button"
            className="bh-draw"
            onClick={() => onOpenDraw(group.round)}
            aria-label={t('historyOpenDraw')}
          >
            {parts.value}
            {parts.tags.map((tag) => <em key={tag.key} className={`bh-dtag tone-${tag.tone}`}>{tag.label}</em>)}
            <i aria-hidden="true">›</i>
          </button>
        ) : null}
      </header>

      {group.bets.map((bet) => <BetRow key={bet.id} bet={bet} />)}

      {/* Only where it adds something: a single-bet round would repeat its one
          row. This line is what makes the screen reconcilable. */}
      {group.bets.length > 1 ? (
        <footer>
          <span>{t('historyRoundBets').replace('%n%', String(group.bets.length))} · {fmtMoney(group.stake)}</span>
          {group.settled ? <b className={netClass(group.net)}>{signed(group.net)}</b> : <b className="pending-text">{t('pending')}</b>}
        </footer>
      ) : null}
    </article>
  );
}

function BetRow({ bet }: { bet: UiBet }) {
  const t = useI18nStore((state) => state.t);
  const pending = bet.result === 'pending';
  return (
    <div className={`bh-row result-${bet.result}`}>
      <span className="bh-pick">{pickLabel(bet.game, bet.tab, bet.pick, t) || '--'}</span>
      <span className="bh-stake">{fmtMoney(bet.amount)}</span>
      {/* Net, always. The old room sheet printed the PAYOUT on wins and the
          STAKE on losses, so the rows never summed to the round total they sat
          under: +24 +19 −2 −2 −2 read as +37 beneath a header saying +23. */}
      <span className={`bh-net ${pending ? 'idle' : netClass(bet.net)}`}>{pending ? '—' : signed(bet.net)}</span>
    </div>
  );
}

function SheetState({
  icon: Icon,
  title,
  actionLabel,
  onAction,
}: {
  icon: typeof List;
  title: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="wallet-empty compact sheet-state-react">
      <Icon size={24} aria-hidden="true" />
      <strong>{title}</strong>
      {actionLabel && onAction ? <button type="button" onClick={onAction}>{actionLabel}</button> : null}
    </div>
  );
}

/* ---------------------------------------------------------------- draw chip */

type DrawTag = { key: string; label: string; tone: string };
type DrawParts = { value: JSX.Element; tags: DrawTag[] };

/**
 * What the round produced, rendered per game. A digit on its own is not enough
 * — someone who bet Grande would have to work out that 8 is Grande — and the
 * derived labels on their own are not enough either, because someone who bet
 * the number 8 needs to see the number. Both, using the game semantic colours
 * the bet options and the room trail already use.
 *
 * Returns null while the round has no public result, which is what puts the
 * card into its waiting state.
 */
function drawParts(game: string, draw: BetDraw | undefined, t: (key: any) => string): DrawParts | null {
  if (!draw) return null;

  if (game === 'sicbo') {
    const dice = draw.sicbo;
    if (!dice || dice.length !== 3) return null;
    const [d1, d2, d3] = dice;
    const sum = d1 + d2 + d3;
    const triple = d1 === d2 && d2 === d3;
    return {
      value: <b className="bh-sum">{d1}+{d2}+{d3}={sum}</b>,
      // A triple is its own outcome and does NOT settle Grande/Pequeno, so
      // labelling it "Grande" would state something the payout contradicts.
      tags: triple
        ? [{ key: 'triple', label: t('sicboTripleN').replace('%n%', String(d1)), tone: 'violet' }]
        : [{ key: 'bs', label: pickLabel('sicbo', 'sicbo-bs', sum >= 11 ? 'big' : 'small', t), tone: sum >= 11 ? 'gold' : 'cyan' }],
    };
  }

  if (game === 'roulette') {
    const n = draw.roulette;
    if (n === undefined || n === null) return null;
    const zero = n === 0;
    const red = ROULETTE_REDS.has(n);
    return {
      value: <b className={`bh-digit round ${zero ? 'zero' : red ? 'red' : 'black'}`}>{n}</b>,
      tags: zero ? [] : [
        { key: 'rb', label: pickLabel('roulette', 'roulette-rb', red ? 'red' : 'black', t), tone: red ? 'red' : 'dark' },
        { key: 'oe', label: pickLabel('roulette', 'roulette-oe', n % 2 === 0 ? 'even' : 'odd', t), tone: n % 2 === 0 ? 'blue' : 'rose' },
      ],
    };
  }

  if (game === 'milhar') {
    const digits = draw.milhar;
    if (!digits) return null;
    return { value: <b className="bh-digit bh-milhar">{digits}</b>, tags: [] };
  }

  // 显式判定，不再兜底。以前这里是 lastdigit 的兜底分支，于是任何未知 game
  // 都会显示 D1 的数字并被标成「Último Dígito」—— 不崩不报错，只是默默给
  // 玩家看错误的开奖数。渲染不出来胜过渲染错。
  if (game !== 'lastdigit') return null;

  const n = draw.lastdigit;
  if (n === undefined || n === null) return null;
  const big = n >= 5;
  const even = n % 2 === 0;
  return {
    value: <b className="bh-digit">{n}</b>,
    tags: [
      { key: 'bs', label: pickLabel('lastdigit', 'big-small', big ? 'big' : 'small', t), tone: big ? 'gold' : 'cyan' },
      { key: 'oe', label: pickLabel('lastdigit', 'odd-even', even ? 'even' : 'odd', t), tone: even ? 'blue' : 'rose' },
    ],
  };
}

const ROULETTE_REDS = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);

/* ------------------------------------------------------------------ helpers */

function normalizeBets(items: BetItem[]): UiBet[] {
  return items.map((item) => {
    const raw = String(item.result || item.status || 'pending');
    const normalized = raw === 'won' ? 'win' : raw === 'lost' ? 'lose' : raw;
    const result: UiBet['result'] =
      normalized === 'win' || normalized === 'lose' || normalized === 'cancelled' ? normalized : 'pending';
    const amount = Number(item.amount || 0);
    const payout = Number(item.payout || 0);
    const draw = item.draw && Object.keys(item.draw).length > 0 ? item.draw : undefined;
    return {
      id: String(item.id || `${item.round_number || item.round}:${item.game}:${item.tab}:${item.pick}:${item.placed_at || item.time}`),
      round: Number(item.round_number || item.round || 0),
      roomCode: String(item.room_code || item.room_id || ''),
      game: String(item.game || 'lastdigit'),
      tab: String(item.tab || ''),
      pick: String(item.pick ?? ''),
      amount,
      payout,
      net: settledBetNet({ amount, payout, result }),
      result,
      time: Number(item.placed_at || item.time || 0),
      anchor: Number(item.cutoff_ts || item.placed_at || item.time || 0),
      draw,
    };
  }).sort((a, b) => b.time - a.time);
}

/** One card per (round, game): the same round runs all three games. */
function groupByRound(bets: UiBet[]): RoundGroup[] {
  const groups = new Map<string, RoundGroup>();
  for (const bet of bets) {
    // Keyed by program, not game: both Milhar tiers report game='milhar' and the
    // hourly draw shares a round number with the 5-minute one it lands on, so a
    // game-keyed card would merge a 4-digit and a 5-digit bet into one round.
    const key = `${bet.round || 0}:${betProgram(bet.game, bet.tab)}`;
    const current = groups.get(key);
    if (!current) {
      groups.set(key, {
        key,
        round: bet.round,
        game: bet.game,
        tab: bet.tab,
        time: bet.time,
        anchor: bet.anchor,
        stake: bet.amount,
        net: bet.net,
        settled: bet.result !== 'pending',
        draw: bet.draw,
        bets: [bet],
      });
      continue;
    }
    current.time = Math.max(current.time, bet.time);
    current.stake += bet.amount;
    current.net += bet.net;
    current.settled = current.settled && bet.result !== 'pending';
    current.draw = current.draw || bet.draw;
    current.bets.push(bet);
  }
  return Array.from(groups.values())
    .map((group) => ({ ...group, bets: group.bets.sort((a, b) => b.time - a.time) }))
    .sort((a, b) => b.time - a.time);
}

/**
 * Rounds bucketed by local calendar day, newest first. Without this the round
 * numbers read as broken: they are a per-local-day 1-288 sequence, so a list
 * spanning two days runs #137, #136, #135, #175.
 */
function groupByDay(rounds: RoundGroup[]): DayGroup[] {
  const days = new Map<string, DayGroup>();
  for (const round of rounds) {
    const key = localDayKey(round.time);
    const day = days.get(key);
    if (day) {
      day.rounds.push(round);
      day.time = Math.max(day.time, round.time);
      day.net += round.net;
      day.settled = day.settled && round.settled;
    } else {
      days.set(key, { key, time: round.time, net: round.net, settled: round.settled, rounds: [round] });
    }
  }
  return Array.from(days.values()).sort((a, b) => b.time - a.time);
}

/**
 * The gaming day a round's `#NNN` belongs to — the anchor country's day, which
 * for a viewer outside that zone is not the day this row is filed under.
 */
function gamingDayOf(group: RoundGroup): string {
  return gamingRound(group.anchor || group.time, programSlotSeconds(group.tab))?.date || '';
}

/**
 * True when this row starts a different gaming day from the row above it.
 *
 * A viewer whose device zone matches the anchor never sees one: their day and
 * the gaming day are the same 24 hours. A viewer an hour west does, once a
 * night — their "today" holds the tail of one gaming day and the head of the
 * next, so the numbers run `#002, #001, #288, #287…`. Unmarked that reads as a
 * broken list; the seam says which day the run below belongs to.
 */
function seamBefore(rounds: RoundGroup[], index: number): boolean {
  if (index === 0) return false;
  const here = gamingDayOf(rounds[index]);
  return Boolean(here) && here !== gamingDayOf(rounds[index - 1]);
}

function dayHeading(day: DayGroup, t: (key: any) => string): string {
  const now = Math.floor(Date.now() / 1000);
  if (day.key === localDayKey(now)) return t('today');
  if (day.key === localDayKey(now - 86_400)) return t('yesterday');
  return localDayShort(day.time);
}

function scopeLabel(scope: Scope, t: (key: any) => string): string {
  if (scope.kind === 'room') return t('historyScopeRoom').replace('%room%', scope.label);
  if (scope.kind === 'game') return t(gameNameKey(scope.game));
  return t('historyScopeAll');
}

function filterLabelKey(filter: Filter) {
  if (filter === 'win') return 'historyFilterWin' as const;
  if (filter === 'lose') return 'historyFilterLose' as const;
  if (filter === 'pending') return 'historyFilterPending' as const;
  return 'historyFilterAll' as const;
}

function netClass(net: number) {
  return net > 0 ? 'win-text' : net < 0 ? 'lose-text' : '';
}

function signed(net: number) {
  return `${net > 0 ? '+' : ''}${fmtMoney(net)}`;
}
